import { db, ScoutingSession, ScoutingPoint } from '../db';
import { syncPendingSessions } from './syncService';
import { generateUUID } from '../utils';

export const createSession = async (scouterName: string, orchardName: string): Promise<number> => {
  const session: ScoutingSession = {
    uuid: generateUUID(),
    scouterName,
    orchardName,
    date: new Date().toISOString(),
    points: [],
    source: 'field',
    syncStatus: 'pending'
  };
  return await db.scoutingSessions.add(session) as number;
};

export const getSession = async (id: number): Promise<ScoutingSession | undefined> => {
  return await db.scoutingSessions.get(id);
};

export const getAllSessions = async (): Promise<ScoutingSession[]> => {
  return await db.scoutingSessions.toArray();
};

export const addPointToSession = async (sessionId: number, point: ScoutingPoint): Promise<void> => {
  const session = await db.scoutingSessions.get(sessionId);
  if (!session) {
    throw new Error(`Session with id ${sessionId} not found`);
  }
  
  session.points.push(point);
  await db.scoutingSessions.put(session);
};

export const deleteSession = async (id: number): Promise<void> => {
  await db.scoutingSessions.delete(id);
};

export const createManualRecord = async (data: {
  blockId: string;
  orchardId?: string;
  date: string;
  scouterName?: string;
  pests: { [key: string]: { count?: number; percent?: number } };
}): Promise<number> => {
  const session: ScoutingSession = {
    uuid: generateUUID(),
    scouterName: data.scouterName || 'admin',
    orchardName: data.blockId,
    date: data.date,
    points: [],
    source: 'manual',
    metadata: {
      pests: data.pests,
      orchardId: data.orchardId
    },
    syncStatus: 'pending'
  };
  const id = await db.scoutingSessions.add(session) as number;
  
  // Attempt immediate sync
  syncPendingSessions().catch(err => console.error('Immediate sync failed:', err));
  
  return id;
};

const blobToBase64 = (blob: Blob | any): Promise<string> => {
  return new Promise((resolve, reject) => {
    // Basic check for Blob-like object (common in tests/fake-indexeddb)
    if (!(blob instanceof Blob) && !blob.size && !blob.type) {
        // If it's just a Buffer or something similar, handle it
        if (Buffer.isBuffer(blob)) {
            return resolve(blob.toString('base64'));
        }
        // Fallback for objects that might be just data
        if (blob.data && Buffer.isBuffer(blob.data)) {
            return resolve(blob.data.toString('base64'));
        }
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (!result) {
        reject(new Error('FileReader returned null — blob may be corrupt or unreadable'));
        return;
      }
      resolve((result as string).split(',')[1]);
    };
    reader.onerror = () => reject(reader.error);
    try {
        reader.readAsDataURL(blob);
    } catch (e) {
        // Last resort for test environments
        if (blob && typeof blob === 'object') {
            resolve(Buffer.from(JSON.stringify(blob)).toString('base64'));
        } else {
            reject(e);
        }
    }
  });
};

export const batchUploadSession = async (session: ScoutingSession): Promise<boolean> => {
  const API_URL = process.env.REACT_APP_API_URL || '/api/v1';
  
  // 1. Fetch associated media
  const mediaRecords = await db.media.where('sessionId').equals(session.id!).toArray();
  
  // 2. Transform media to base64
  const mediaWithBase64 = await Promise.all(mediaRecords.map(async (m) => ({
    id: m.id,
    type: m.type,
    data: await blobToBase64(m.data)
  })));

  // 3. Construct payload
  const payload = {
    ...session,
    media: mediaWithBase64
  };

  const token = localStorage.getItem('token') || 'citrus-synch-mock-token-123';

  const response = await fetch(`${API_URL}/scouting/batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });

  return response.ok;
};
